import { SectionHeader } from "@/components/common/section-components/SectionHeader";
import { ConfirmModal } from "@/components/modal/ConfirmModal";
import { ConfirmFertSlotRequestOrderModal } from "@/components/modal/ConfirmFertSlotRequestOrderModal";
import { NeedInfoModal } from "@/components/modal/NeedInfoModal";
import { RejectRequestModal } from "@/components/modal/RejectRequestModal";
import { ModuleTypes, RequestTypes } from "@/constants/enums";
import { SuppliesOrderStatus, SuppliesRequestStatus } from "@/constants/entity-status";
import { AppRoutes } from "@/constants/routes";
import { useRoles } from "@/hooks/useRoles";
import { getChoferes, getEquiposDisponibles } from "@/services/aux-entities.service";
import { Flex, Text, useDisclosure } from "@chakra-ui/react";
import { useEffect, useState } from "react";
import { FieldValues } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";

import { FuelSlotRequestActions } from "@/components/common/section-components/requests/fuel-slot-request/FuelSlotRequestActions";
import { FuelSlotRequestMainData } from "@/components/common/section-components/requests/fuel-slot-request/FuelSlotRequestMainData";
import { FuelSlotRequestMessagesTable } from "@/components/common/section-components/requests/fuel-slot-request/FuelSlotRequestMessagesTable";
import {
  FuelSlotRequestOrdersTable,
  FuelSlotRequestOrdersTableItem,
} from "@/components/common/section-components/requests/fuel-slot-request/FuelSlotRequestOrdersTable";
import { FuelSlotRequestProductsTable } from "@/components/common/section-components/requests/fuel-slot-request/FuelSlotRequestProductsTable";
import {
  FuelSlotRequestDetail,
  FuelSlotRequestOrder,
  FuelSlotRequestProduct,
} from "./schema/fuel-slot-request-detail.schema";
import {
  confirmOrderHandler,
  fetchDetailHandler,
  fetchOrdersHandler,
  handleCreateOrderHandler,
  handleEditOrdersHandler,
  rejectHandler,
  reassignOrderHandler,
  sendInfoHandler,
} from "./schema/handler-functions";
import { toaster } from "@/components/ui/toaster";

const DetailLegend = ({ title }: { title: string }) => {
  return (
    <Flex alignItems="center" gap="30px">
      <Text fontWeight={700}>{title}</Text>
    </Flex>
  );
};

export const FuelSlotRequestDetailPage = () => {
  const [isActive, setIsActive] = useState(false);

  useEffect(() => {
    setIsActive(true);
  }, []);

  const { id } = useParams();
  const navigate = useNavigate();
  const { fetchPermissions } = useRoles();

  const [loadingDetail, setLoadingDetail] = useState(false);
  const [loadingOrders, setLoadingOrders] = useState(false);
  const [loadingSendInfo, setLoadingSendInfo] = useState(false);
  const [loadingReject, setLoadingReject] = useState(false);
  const [loadingConfirmOrderId, setLoadingConfirmOrderId] = useState<
    number | null
  >(null);

  const [detail, setDetail] = useState<FuelSlotRequestDetail | null>(null);
  const [orders, setOrders] = useState<FuelSlotRequestOrder[] | undefined>(
    undefined,
  );

  const { open: openInfoModal, setOpen: setOpenInfoModal } = useDisclosure();
  const { open: openRejectModal, setOpen: setOpenRejectModal } =
    useDisclosure();

  const { open: openConfirmOrderModal, setOpen: setOpenConfirmOrderModal } =
    useDisclosure();
  const [selectedConfirmOrderId, setSelectedConfirmOrderId] = useState<
    number | null
  >(null);

  const [driverOptions, setDriverOptions] = useState<
    { label: string; value: number }[]
  >([]);
  const [equipmentOptions, setEquipmentOptions] = useState<
    { label: string; value: number }[]
  >([]);
  const [loadingAuxOptions, setLoadingAuxOptions] = useState<boolean>(false);
  const [loadingEquipments, setLoadingEquipments] = useState<boolean>(false);

  const [openConfirmLastPending, setOpenConfirmLastPending] =
    useState<boolean>(false);
  const [pendingConfirmPayload, setPendingConfirmPayload] = useState<{
    orderId: number;
    driverId: number;
    equipmentId: number;
    deliveryDate: string;
  } | null>(null);

  const handleBack = () => {
    navigate(AppRoutes.FUEL_SLOTS_REQ);
  };

  const fetchDetail = async (showLoad = true) => {
    await fetchDetailHandler({
      id,
      navigate,
      fetchPermissions,
      setLoadingDetail,
      setDetail,
      showLoad,
    });
  };

  const fetchOrders = async (showLoad = true) => {
    await fetchOrdersHandler({
      id,
      navigate,
      fetchPermissions,
      setLoadingOrders,
      setOrders,
      showLoad,
    });
  };

  useEffect(() => {
    if (id && isActive) {
      fetchDetail();
      fetchOrders();
    }
  }, [id, isActive]);

  useEffect(() => {
    if (!isActive) return;

    let cancelled = false;

    const fetchAuxOptions = async () => {
      try {
        setLoadingAuxOptions(true);
        const drivers = await getChoferes();

        if (cancelled) return;

        setDriverOptions(
          (drivers ?? []).map((d) => ({
            label: d.nombreApellido,
            value: d.id,
          })),
        );

        setEquipmentOptions([]);
      } catch {
        if (cancelled) return;
        setDriverOptions([]);
        setEquipmentOptions([]);
        toaster.create({
          type: "error",
          title: "No se pudieron cargar los choferes.",
        });
      } finally {
        if (!cancelled) setLoadingAuxOptions(false);
      }
    };

    fetchAuxOptions();

    return () => {
      cancelled = true;
    };
  }, [isActive]);

  const fetchEquiposByChoferAndDate = async (params: {
    driverId: number | null;
    deliveryDate: string | null;
  }) => {
    if (!params.driverId || !params.deliveryDate) {
      setEquipmentOptions([]);
      setLoadingEquipments(false);
      return;
    }

    try {
      setLoadingEquipments(true);
      const equipments = await getEquiposDisponibles({
        idChofer: params.driverId,
        fechaEntrega: params.deliveryDate,
        tipoModulo: ModuleTypes.Combustible
      });
      setEquipmentOptions(
        (equipments ?? []).map((e) => ({
          label: `${e.nombre}${e.patente ? " - " + e.patente : ""}`,
          value: e.id,
        })),
      );
    } catch {
      setEquipmentOptions([]);
      toaster.create({
        type: "error",
        title: "No se pudieron cargar los equipos.",
      });
    } finally {
      setLoadingEquipments(false);
    }
  };

  const productsTableItems =
    detail?.productos?.map((p: FuelSlotRequestProduct) => ({
      ...p,
      cantidad: `${new Intl.NumberFormat("es-AR").format(p?.cantidad ?? 0)}`,
    })) ?? [];

  const ordersTableItems: FuelSlotRequestOrdersTableItem[] = (orders ??
    []) as any;

  const handleSendInfo = async (event?: FieldValues) => {
    await sendInfoHandler({
      id,
      event,
      navigate,
      fetchPermissions,
      setLoadingSendInfo,
      setOpenInfoModal,
      fetchDetail,
    });
  };

  const handleReject = async (event?: FieldValues) => {
    await rejectHandler({
      id,
      event,
      navigate,
      fetchPermissions,
      setLoadingReject,
      setOpenRejectModal,
      fetchDetail,
    });
  };

  const handleCreateOrder = () => {
    handleCreateOrderHandler(navigate, id);
  };

  const handleEditOrders = () => {
    handleEditOrdersHandler(navigate, id);
  };

  const handleOpenConfirmOrderModal = (orderId: number) => {
    setSelectedConfirmOrderId(orderId);
    setOpenConfirmOrderModal(true);
  };

  const handleConfirmOrderSubmit = async (payload: {
    orderId: number;
    driverId: number;
    equipmentId: number;
    deliveryDate: string;
  }) => {
    const currentOrder = (orders ?? []).find(
      (o: any) => Number(o?.id ?? 0) === Number(payload.orderId),
    ) as any;
    const canReassignOrder =
      currentOrder?.estado === SuppliesOrderStatus.NOT_DELIVERED &&
      (currentOrder?.intentos ?? []).length >= 1;

    if (canReassignOrder) {
      await reassignOrderHandler({
        orderId: payload.orderId,
        body: {
          fechaEntrega: payload.deliveryDate,
          idChofer: Number(payload.driverId),
          idEquipo: Number(payload.equipmentId),
        },
        id,
        navigate,
        fetchPermissions,
        setLoadingConfirmOrderId,
        fetchOrders,
      });

      fetchDetail(false);
      return;
    }

    const pendingOrders = (orders ?? []).filter(
      (o) => o?.estado === SuppliesOrderStatus.PENDING,
    );

    if (pendingOrders.length === 1) {
      setPendingConfirmPayload(payload);
      setOpenConfirmLastPending(true);
      return;
    }

    await confirmOrderHandler({
      orderId: payload.orderId,
      body: {
        fechaEntrega: payload.deliveryDate,
        idChofer: Number(payload.driverId),
        idEquipo: Number(payload.equipmentId),
      },
      id,
      navigate,
      fetchPermissions,
      setLoadingConfirmOrderId,
      fetchOrders,
    });

    fetchDetail(false);
  };

  const handleAcceptConfirmLastPending = async () => {
    if (!pendingConfirmPayload) return;

    await confirmOrderHandler({
      orderId: pendingConfirmPayload.orderId,
      body: {
        fechaEntrega: pendingConfirmPayload.deliveryDate,
        idChofer: Number(pendingConfirmPayload.driverId),
        idEquipo: Number(pendingConfirmPayload.equipmentId),
      },
      id,
      navigate,
      fetchPermissions,
      setLoadingConfirmOrderId,
      fetchOrders,
    });

    setOpenConfirmLastPending(false);
    setPendingConfirmPayload(null);
    fetchDetail(false);
  };

  const loading =
    loadingDetail ||
    loadingOrders ||
    loadingSendInfo ||
    loadingReject ||
    loadingConfirmOrderId !== null;

  const hasOrdersLoaded = (orders ?? []).length >= 1;
  const hasAnyOrderWithDriverAssigned = (orders ?? []).some((o: any) => !!o?.chofer);

  const canCreateOrder =
    !hasOrdersLoaded &&
    (detail?.estado === SuppliesRequestStatus.PENDING ||
      detail?.estado === SuppliesRequestStatus.NEEDS_INFO);

  const canSendInfo =
    !hasAnyOrderWithDriverAssigned &&
    (detail?.estado === SuppliesRequestStatus.PENDING ||
      detail?.estado === SuppliesRequestStatus.NEEDS_INFO);

  const canReject =
    !hasAnyOrderWithDriverAssigned &&
    (detail?.estado === SuppliesRequestStatus.PENDING ||
      detail?.estado === SuppliesRequestStatus.NEEDS_INFO ||
      detail?.estado === SuppliesRequestStatus.ORDER_CREATED ||
      (detail?.estado !== SuppliesRequestStatus.DELIVERED &&
        detail?.estado !== SuppliesRequestStatus.PARTIAL_DELIVERY &&
        detail?.estado !== SuppliesRequestStatus.IN_TRANSIT &&
        detail?.estado !== SuppliesRequestStatus.PARTIALLY_CONFIRMED &&
        detail?.estado !== SuppliesRequestStatus.REJECTED &&
        detail?.estado !== SuppliesRequestStatus.CANCELLED));

  const hasAnyEditablePendingOrder = (orders ?? []).some((o: any) => {
    const isPending = o?.estado === SuppliesOrderStatus.PENDING;
    const hasDriverAssigned = !!o?.chofer;
    return isPending && !hasDriverAssigned;
  });

  const canEditOrders =
    hasAnyEditablePendingOrder &&
    (detail?.estado === SuppliesRequestStatus.ORDER_CREATED ||
      detail?.estado === SuppliesRequestStatus.PARTIALLY_CONFIRMED ||
      detail?.estado === SuppliesRequestStatus.IN_TRANSIT);

  return (
    <Flex
      flexDir="column"
      paddingTop="20px"
      gap="12px"
      height="100%"
      minHeight={0}
    >
      <SectionHeader
        title="Solicitud de combustible"
        onBack={handleBack}
        backButton
        loading={loading}
      />

      {!loadingDetail && (
        <Flex
          flex="1"
          minHeight={0}
          overflowY="auto"
          flexDir="column"
          gap="12px"
          paddingRight="8px"
          paddingBottom="16px"
        >
          <FuelSlotRequestProductsTable
            items={productsTableItems}
            rightElement={
              <FuelSlotRequestActions
                showReject={canReject}
                showNeedsInfo={canSendInfo}
                showCreateOrder={canCreateOrder}
                loadingSendInfo={loadingSendInfo}
                loadingReject={loadingReject}
                setOpenInfoModal={setOpenInfoModal}
                setOpenRejectModal={setOpenRejectModal}
                handleCreateOrder={handleCreateOrder}
              />
            }
          />

          <FuelSlotRequestMainData
            fechaEntrega={detail?.fechaEntrega}
            titularCuenta={detail?.titularCuenta}
            destino={detail?.destino}
            contacto={detail?.contacto}
            estadoDescripcion={detail?.estado}
          />

          <DetailLegend title="Mensajes" />

          <Flex flexDir="column" flexShrink={0}>
            <FuelSlotRequestMessagesTable messages={detail?.mensajes || []} />
          </Flex>

          {Boolean(orders?.length) && (
            <FuelSlotRequestOrdersTable
              orders={ordersTableItems}
              loadingConfirmOrderId={loadingConfirmOrderId}
              onConfirmOrder={handleOpenConfirmOrderModal}
              showEditOrders={canEditOrders}
              onEditOrders={handleEditOrders}
            />
          )}
        </Flex>
      )}

      {detail?.id && (
        <>
          <ConfirmModal
            openConfirm={openConfirmLastPending}
            setOpenConfirm={setOpenConfirmLastPending}
            handleAccept={handleAcceptConfirmLastPending}
            loadingAccept={
              pendingConfirmPayload
                ? loadingConfirmOrderId === pendingConfirmPayload.orderId
                : false
            }
            title="Asignación de solicitud"
            subtitle="Si asignas este pedido, ya no podrás volver a agregar o editar pedidos"
          />

          {selectedConfirmOrderId !== null && (
            <ConfirmFertSlotRequestOrderModal
              open={openConfirmOrderModal}
              setOpen={(flag) => {
                setOpenConfirmOrderModal(flag);
                if (!flag) setSelectedConfirmOrderId(null);
              }}
              orderId={selectedConfirmOrderId}
              loadingConfirm={loadingConfirmOrderId === selectedConfirmOrderId}
              titleText={(() => {
                const currentOrder = (orders ?? []).find(
                  (o: any) => Number(o?.id ?? 0) === Number(selectedConfirmOrderId),
                ) as any;
                const canReassignOrder =
                  currentOrder?.estado === SuppliesOrderStatus.NOT_DELIVERED &&
                  (currentOrder?.intentos ?? []).length >= 1;
                return canReassignOrder ? "Reasignar pedido" : "Asignar pedido";
              })()}
              confirmButtonLabel={(() => {
                const currentOrder = (orders ?? []).find(
                  (o: any) => Number(o?.id ?? 0) === Number(selectedConfirmOrderId),
                ) as any;
                const canReassignOrder =
                  currentOrder?.estado === SuppliesOrderStatus.NOT_DELIVERED &&
                  (currentOrder?.intentos ?? []).length >= 1;
                return canReassignOrder ? "Reasignar" : "Asignar";
              })()}
              loadingDrivers={loadingAuxOptions}
              loadingEquipments={loadingAuxOptions || loadingEquipments}
              onDriverOrDateChange={fetchEquiposByChoferAndDate}
              handleConfirmOrder={handleConfirmOrderSubmit}
              driverOptions={driverOptions}
              equipmentOptions={equipmentOptions}
            />
          )}
          <NeedInfoModal
            open={openInfoModal}
            setOpen={setOpenInfoModal}
            handleAccept={handleSendInfo}
            loadingAccept={loadingSendInfo}
            idTipoSolicitud={RequestTypes.CuposCombustible}
          />
          <RejectRequestModal
            open={openRejectModal}
            setOpen={setOpenRejectModal}
            handleAccept={handleReject}
            loadingAccept={loadingReject}
            idTipoSolicitud={RequestTypes.CuposCombustible}
            entityName="solicitud de combustible"
          />
        </>
      )}
    </Flex>
  );
};
